import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import BackendService from '../services/backendService';
import {
  SupportedIntegrationLabel,
  SupportedIntegrationResponse,
} from '../services/backendService/types';

const useGoogleDrive = (tenantId: string) => {
  const queryClient = useQueryClient();

  // Initialize react query for all integrations, with a refetch interval
  const googleDriveQuery = useQuery(
    ['integrations', SupportedIntegrationLabel.GoogleDrive],
    async ({ queryKey }) => {
      // If we already have data with an access token, use refresh endpoint
      const existingData:
        | SupportedIntegrationResponse
        | undefined = queryClient.getQueryData(queryKey);
      if (existingData?.accessToken) {
        return await BackendService.instance.integrations.refreshMetadata(
          SupportedIntegrationLabel.GoogleDrive,
          tenantId,
        );
      }
      // Otherwise, get initial metadata
      return await BackendService.instance.integrations.getMetadata(
        SupportedIntegrationLabel.GoogleDrive,
        tenantId,
      );
    },
    {
      refetchInterval(data) {
        const localUTC = new Date(new Date().toISOString());
        const refreshIn = data?.accessToken?.expiresAt
          ? new Date(data.accessToken.expiresAt).getTime() - localUTC.getTime() - 60000
          : 300000;
        // Ensure that we don't refresh with a negative (or 0) interval
        return Math.max(refreshIn, 1000);
      },
      refetchIntervalInBackground: true,
    },
  );

  const authenticate = useCallback(async () => {
    if (!googleDriveQuery.data || googleDriveQuery.isLoading || googleDriveQuery.error) {
      return;
    }
    const authorizationUrl: string = googleDriveQuery.data.authorizationUrl;
    const authWindow = window.open(
      authorizationUrl,
      'Integration Authentication',
      'width=500,height=600',
    );
    // Focus the window and wait for it to close
    if (authWindow) {
      authWindow.focus();
      await new Promise<void>(() => {
        const checkWindowClosed = setInterval(() => {
          if (authWindow.closed) {
            clearInterval(checkWindowClosed);
            return googleDriveQuery.refetch();
          }
        }, 1000);
      });
    }
  }, [googleDriveQuery]);

  return {
    metadata: googleDriveQuery.data,
    refresh: googleDriveQuery.refetch,
    authenticate,
    isReady: googleDriveQuery.isSuccess,
    isLoading: googleDriveQuery.isLoading,
    isError: !!googleDriveQuery.error,
  };
};

export default useGoogleDrive;
